import { useCallback, useContext, useEffect, useState } from 'react';
import { Container } from 'react-bootstrap';
import StyleAttributeTable from '../../components/StyleAttributeTable';
import { AuthContext } from '../../context/AuthContext';
import Spinner from '../Spinner';
import DeleteModal from "../modals/DeleteModal";

const BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:3000';

const FitManagement = ({ embedded, styleNumber, companyCode, onSuccess, onCancel }) => {
  const { userData } = useContext(AuthContext);
  const [fits, setFits] = useState([]);
  const [pageLoading, setPageLoading] = useState(true); // Page loading state
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [isEditing, setIsEditing] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [formData, setFormData] = useState({
    fit_name: "",
    description: "",
  });

  const columns = [
    { key: "fit_name", label: "Fit Name" },
    { key: "description", label: "Description" },
  ];

  // Delete modal state
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteFitId, setDeleteFitId] = useState(null);

  const fetchFits = useCallback(async () => {
    setPageLoading(true);
    try {
      const response = await fetch(
        `${BASE_URL}/api/admin/fits/get-fits?company_code=${userData.company_code}`
      );
      const data = await response.json();
      if (data.success) {
        setFits(data.fits);
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError("Error fetching fits");
    }
    setPageLoading(false);
  }, [userData.company_code]);

  useEffect(() => {
    fetchFits();
  }, [fetchFits]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const url = isEditing
        ? `${BASE_URL}/api/admin/fits/update-fits/${editingId}`
        : `${BASE_URL}/api/admin/fits/add-fits`;
      const response = await fetch(url, {
        method: isEditing ? "PUT" : "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...formData,
          company_code: companyCode || userData.company_code,
        }),
      });
      const data = await response.json();
      if (data.success) {
        setSuccess(
          isEditing ? "Fit updated successfully" : "Fit added successfully"
        );
        fetchFits();
        if (!isEditing && embedded && styleNumber && companyCode) {
          // Assign to style
          let fitId = null;
          if (data.fit && (data.fit.fit_id || data.fit.id)) {
            fitId = data.fit.fit_id || data.fit.id;
          } else if (data.id) {
            fitId = data.id;
          } else if (data.fit_id) {
            fitId = data.fit_id;
          }
          if (fitId) {
            const assignRes = await fetch(
              `${BASE_URL}/api/admin/styles/add-style-attributes`,
              {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                  style_number: styleNumber,
                  company_code: companyCode,
                  type: "fits",
                  attribute_ids: [fitId],
                }),
              }
            );
            const assignData = await assignRes.json();
          } else {
            console.warn("Could not determine new fit ID from response:", data);
          }
        }
        resetForm();
        if (onSuccess) onSuccess();
      } else {
        setError(data.message);
      }
    } catch (err) {
      setError("Error saving fit");
    }
  };

  const handleEdit = (fit) => {
    setIsEditing(true);
    setEditingId(fit.fit_id);
    setFormData({
      fit_name: fit.fit_name,
      description: fit.description || "",
    });
  };

   const handleDeleteClick = (fitId) => {
     setDeleteFitId(fitId);
     setShowDeleteModal(true);
   };

   const handleDeleteSuccess = () => {
     setSuccess("Fit deleted successfully");
     fetchFits();
   };

   const handleDeleteError = (error) => {
     setError(error?.message || "Error deleting fit");
   };


  const resetForm = () => {
    setFormData({ fit_name: "", description: "" });
    setIsEditing(false);
    setEditingId(null);
  };

  return (
    <>
      {pageLoading ? (
        <div
          style={{
            display: "flex",
            justifyContent: "center",
            alignItems: "center",
            minHeight: "400px",
          }}
        >
          <Spinner />
        </div>
      ) : (
        <Container>
          <StyleAttributeTable
            title="Fits"
            items={fits}
            columns={columns}
            formData={formData}
            setFormData={setFormData}
            handleSubmit={handleSubmit}
            handleEdit={handleEdit}
            handleDelete={handleDeleteClick}
            isEditing={isEditing}
            error={error}
            success={success}
            onCancel={resetForm}
            embedded={embedded}
          />

          <DeleteModal
            show={showDeleteModal}
            onHide={() => setShowDeleteModal(false)}
            id={deleteFitId}
            deleteUrl={`/api/admin/fits/delete-fits/:id`}
            entityLabel="fit"
            modalTitle="Delete Fit"
            onDeleteSuccess={handleDeleteSuccess}
            onDeleteError={handleDeleteError}
          />
        </Container>
      )}
    </>
  );
};

export default FitManagement;
